import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Clock3,
  Filter,
  GraduationCap,
  RotateCcw,
  Search,
  Star,
  Trash2,
  XCircle,
} from 'lucide-react';
import {
  generateTutorialSteps,
  TutorialStepSource,
  LearningCourse,
  LearningLesson,
  listLearningCourses,
  listLearningLessons,
  listTutorialDefinitions,
  TutorialDefinition,
  updateTutorialDefinitionStatus,
  deleteTutorialDefinition,
  listTutorialRuns,
  TutorialRun,
} from '../api/services';
import PdfPreviewModal from '../components/PdfPreviewModal';
import './Tutorials.css';

const pageSize = 25;
const intelligencePageSize = 100;
const maxIntelligencePages = 40;

type TutorialIntelligenceRow = {
  key: string;
  tutorialKey: string;
  appName: string;
  title: string;
  runs: number;
  completionRate: number;
  stepCompletionRate: number;
  hotspotQualityScore: number;
  flowQualityScore: number;
  ratingScore: number;
  confidenceScore: number;
  confidenceLabel: 'high' | 'medium' | 'low';
  workingCorrectly: boolean;
  topFailureReason: string | null;
};

type IntelligenceConfig = {
  minRuns: number;
  confidenceThreshold: number;
  completionThreshold: number;
  stepThreshold: number;
  hotspotThreshold: number;
  flowThreshold: number;
  ratingThreshold: number;
  completionWeight: number;
  stepWeight: number;
  hotspotWeight: number;
  flowWeight: number;
  ratingWeight: number;
};

type IntelligencePresetName = 'strict' | 'balanced' | 'lenient';

const defaultIntelligenceConfig: IntelligenceConfig = {
  minRuns: 5,
  confidenceThreshold: 0.85,
  completionThreshold: 0.85,
  stepThreshold: 0.85,
  hotspotThreshold: 0.75,
  flowThreshold: 0.8,
  ratingThreshold: 0.8,
  completionWeight: 0.3,
  stepWeight: 0.2,
  hotspotWeight: 0.2,
  flowWeight: 0.1,
  ratingWeight: 0.2,
};

const intelligencePresets: Record<IntelligencePresetName, IntelligenceConfig> = {
  strict: {
    minRuns: 8,
    confidenceThreshold: 0.9,
    completionThreshold: 0.9,
    stepThreshold: 0.9,
    hotspotThreshold: 0.85,
    flowThreshold: 0.85,
    ratingThreshold: 0.85,
    completionWeight: 0.3,
    stepWeight: 0.2,
    hotspotWeight: 0.25,
    flowWeight: 0.1,
    ratingWeight: 0.15,
  },
  balanced: defaultIntelligenceConfig,
  lenient: {
    minRuns: 3,
    confidenceThreshold: 0.72,
    completionThreshold: 0.7,
    stepThreshold: 0.72,
    hotspotThreshold: 0.6,
    flowThreshold: 0.65,
    ratingThreshold: 0.65,
    completionWeight: 0.28,
    stepWeight: 0.22,
    hotspotWeight: 0.18,
    flowWeight: 0.12,
    ratingWeight: 0.2,
  },
};

function intelligenceConfigEquals(a: IntelligenceConfig, b: IntelligenceConfig): boolean {
  return (
    a.minRuns === b.minRuns &&
    a.confidenceThreshold === b.confidenceThreshold &&
    a.completionThreshold === b.completionThreshold &&
    a.stepThreshold === b.stepThreshold &&
    a.hotspotThreshold === b.hotspotThreshold &&
    a.flowThreshold === b.flowThreshold &&
    a.ratingThreshold === b.ratingThreshold &&
    a.completionWeight === b.completionWeight &&
    a.stepWeight === b.stepWeight &&
    a.hotspotWeight === b.hotspotWeight &&
    a.flowWeight === b.flowWeight &&
    a.ratingWeight === b.ratingWeight
  );
}

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleString() : 'Not recorded';
}

function formatVideoTime(seconds: number) {
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

function statusLabel(status: TutorialRun['status']) {
  return status.replace('_', ' ');
}

function getHotspotSummary(hotspot: unknown): string {
  if (!hotspot || typeof hotspot !== 'object') return 'No hotspot metadata';
  const candidate = hotspot as Record<string, unknown>;
  const title = typeof candidate.title === 'string' ? candidate.title : 'Untitled hotspot';
  const role = typeof candidate.role === 'string' ? candidate.role : 'Unknown role';
  const rank = typeof candidate.rank === 'number' ? ` #${candidate.rank}` : '';
  const confidence = typeof candidate.confidence === 'number' ? ` (${Math.round(candidate.confidence * 100)}%)` : '';
  return `${title} [${role}]${rank}${confidence}`;
}

function getShownHotspotCount(shownHotspots: unknown): number {
  if (!Array.isArray(shownHotspots)) return 0;
  return shownHotspots.length;
}

function getHotspotConfidence(hotspot: unknown): number {
  if (!hotspot || typeof hotspot !== 'object') return 0;
  const candidate = hotspot as Record<string, unknown>;
  const confidence = typeof candidate.confidence === 'number' ? candidate.confidence : 0;
  const baseConfidence = typeof candidate.baseConfidence === 'number' ? candidate.baseConfidence : 0;
  const learnedConfidence = typeof candidate.learnedConfidence === 'number' ? candidate.learnedConfidence : 0;
  return Math.max(confidence, baseConfidence, learnedConfidence);
}

function getHotspotRank(hotspot: unknown): number | null {
  if (!hotspot || typeof hotspot !== 'object') return null;
  const candidate = hotspot as Record<string, unknown>;
  return typeof candidate.rank === 'number' ? candidate.rank : null;
}

function getRunHotspotScore(run: TutorialRun): number {
  if (run.steps.length === 0) return 0;

  let totalScore = 0;
  for (const step of run.steps) {
    let stepScore = 0;
    if (step.clickedHotspot && typeof step.clickedHotspot === 'object') stepScore += 0.4;

    const confidence = getHotspotConfidence(step.clickedHotspot);
    if (confidence >= 0.7) stepScore += 0.3;
    else if (confidence >= 0.5) stepScore += 0.15;

    const rank = getHotspotRank(step.clickedHotspot);
    if (rank !== null && rank <= 2) stepScore += 0.2;
    else if (rank !== null && rank <= 4) stepScore += 0.1;

    const shownCount = getShownHotspotCount(step.shownHotspots);
    if (shownCount > 0) stepScore += 0.1;

    totalScore += Math.min(stepScore, 1);
  }

  return totalScore / run.steps.length;
}

function getRunFlowScore(run: TutorialRun): number {
  const statusScore = run.status === 'completed' ? 1 : run.status === 'in_progress' ? 0.4 : 0.2;
  if (run.steps.length === 0) return statusScore;

  const completedSteps = run.steps.filter((step) => step.completed).length;
  const stepScore = completedSteps / run.steps.length;
  return (statusScore * 0.6) + (stepScore * 0.4);
}

function buildTutorialIntelligenceRows(
  runs: TutorialRun[],
  config: IntelligenceConfig
): TutorialIntelligenceRow[] {
  const groups = new Map<string, TutorialRun[]>();
  for (const run of runs) {
    const key = `${run.appName}::${run.tutorialKey}`;
    const existing = groups.get(key) || [];
    existing.push(run);
    groups.set(key, existing);
  }

  const rows: TutorialIntelligenceRow[] = [];

  for (const [key, groupedRuns] of groups.entries()) {
    const totalRuns = groupedRuns.length;
    const completedRuns = groupedRuns.filter((run) => run.status === 'completed').length;
    const completionRate = totalRuns > 0 ? completedRuns / totalRuns : 0;

    const totalSteps = groupedRuns.reduce((count, run) => count + run.steps.length, 0);
    const completedSteps = groupedRuns.reduce(
      (count, run) => count + run.steps.filter((step) => step.completed).length,
      0
    );
    const stepCompletionRate = totalSteps > 0 ? completedSteps / totalSteps : 0;

    const hotspotQualityScore = groupedRuns.reduce((sum, run) => sum + getRunHotspotScore(run), 0) / totalRuns;
    const flowQualityScore = groupedRuns.reduce((sum, run) => sum + getRunFlowScore(run), 0) / totalRuns;

    const ratedRuns = groupedRuns.filter((run) => typeof run.rating === 'number');
    const averageRating = ratedRuns.length > 0
      ? ratedRuns.reduce((sum, run) => sum + Number(run.rating), 0) / ratedRuns.length
      : null;
    const ratingScore = averageRating === null ? 0.6 : Math.min(Math.max(averageRating / 5, 0), 1);

    const failureReasonCounts = new Map<string, number>();
    for (const run of groupedRuns) {
      if (!run.failureReasonCode) continue;
      failureReasonCounts.set(run.failureReasonCode, (failureReasonCounts.get(run.failureReasonCode) || 0) + 1);
    }

    const sortedFailureReasons = Array.from(failureReasonCounts.entries()).sort((a, b) => b[1] - a[1]);
    const topFailureReason = sortedFailureReasons.length > 0 ? sortedFailureReasons[0][0] : null;

    const totalWeight =
      config.completionWeight +
      config.stepWeight +
      config.hotspotWeight +
      config.flowWeight +
      config.ratingWeight;

    const safeWeight = totalWeight > 0 ? totalWeight : 1;

    const confidenceScore =
      ((completionRate * config.completionWeight) +
      (stepCompletionRate * config.stepWeight) +
      (hotspotQualityScore * config.hotspotWeight) +
      (flowQualityScore * config.flowWeight) +
      (ratingScore * config.ratingWeight)) / safeWeight;

    const confidenceLabel: 'high' | 'medium' | 'low' =
      confidenceScore >= 0.8 ? 'high' : confidenceScore >= 0.65 ? 'medium' : 'low';

    const workingCorrectly =
      totalRuns >= config.minRuns &&
      confidenceScore >= config.confidenceThreshold &&
      completionRate >= config.completionThreshold &&
      stepCompletionRate >= config.stepThreshold &&
      hotspotQualityScore >= config.hotspotThreshold &&
      flowQualityScore >= config.flowThreshold &&
      ratingScore >= config.ratingThreshold;

    rows.push({
      key,
      tutorialKey: groupedRuns[0].tutorialKey,
      appName: groupedRuns[0].appName,
      title: groupedRuns[0].title,
      runs: totalRuns,
      completionRate,
      stepCompletionRate,
      hotspotQualityScore,
      flowQualityScore,
      ratingScore,
      confidenceScore,
      confidenceLabel,
      workingCorrectly,
      topFailureReason,
    });
  }

  return rows.sort((a, b) => b.confidenceScore - a.confidenceScore);
}

export default function Tutorials() {
  const [activeTab, setActiveTab] = useState<'history' | 'intelligence' | 'content' | 'approved'>('history');
  const [runs, setRuns] = useState<TutorialRun[]>([]);
  const [intelligenceRows, setIntelligenceRows] = useState<TutorialIntelligenceRow[]>([]);
  const [intelligenceConfig, setIntelligenceConfig] = useState<IntelligenceConfig>(defaultIntelligenceConfig);
  const [isTuningCollapsed, setIsTuningCollapsed] = useState(false);
  const [pagination, setPagination] = useState({ hasMore: false });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedDefinition, setExpandedDefinition] = useState<string | null>(null);
  const [expandedSoftware, setExpandedSoftware] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);
  const [error, setError] = useState('');
  const [intelligenceError, setIntelligenceError] = useState('');
  const [courses, setCourses] = useState<LearningCourse[]>([]);
  const [lessons, setLessons] = useState<LearningLesson[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>();
  const [contentSearch, setContentSearch] = useState('');
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState('');
  const [contentMessage, setContentMessage] = useState('');
  const contentFeedbackRef = useRef<HTMLDivElement>(null);
  const [pdfPreview, setPdfPreview] = useState<{ title: string; url: string } | null>(null);
  const [definitions, setDefinitions] = useState<TutorialDefinition[]>([]);
  const [generatingStepsKey, setGeneratingStepsKey] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [groupBy, setGroupBy] = useState<'none' | 'software'>('none');
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    platform: '',
    from: '',
    to: '',
  });

  const loadRuns = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await listTutorialRuns({
        status: filters.status || undefined,
        platform: filters.platform || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
        offset: page * pageSize,
        limit: pageSize,
      });
      setRuns(result.runs);
      setPagination(result.pagination);
    } catch (loadError) {
      setError('Unable to load tutorial history. Check the API connection and try again.');
      console.error(loadError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRuns();
  }, [page, filters.status, filters.platform, filters.from, filters.to]);

  const loadIntelligence = async () => {
    setIntelligenceLoading(true);
    setIntelligenceError('');

    try {
      const allRuns: TutorialRun[] = [];
      let currentOffset = 0;
      let hasMore = true;

      for (let index = 0; index < maxIntelligencePages && hasMore; index += 1) {
        const result = await listTutorialRuns({
          status: filters.status || undefined,
          platform: filters.platform || undefined,
          from: filters.from || undefined,
          to: filters.to || undefined,
          offset: currentOffset,
          limit: intelligencePageSize,
        });

        allRuns.push(...result.runs);
        hasMore = Boolean(result.pagination?.hasMore);
        currentOffset += intelligencePageSize;

        if (result.runs.length === 0) break;
      }

      setIntelligenceRows(buildTutorialIntelligenceRows(allRuns, intelligenceConfig));
    } catch (loadError) {
      setIntelligenceError('Unable to calculate tutorial intelligence right now.');
      console.error(loadError);
    } finally {
      setIntelligenceLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'intelligence') return;
    void loadIntelligence();
  }, [activeTab, filters.status, filters.platform, filters.from, filters.to, intelligenceConfig]);

  const loadLearningContent = async () => {
    setContentLoading(true);
    setContentError('');
    setContentMessage('');
    try {
      const courseResult = await listLearningCourses(contentSearch.trim() || undefined);
      setCourses(courseResult.courses);
      setDefinitions(await listTutorialDefinitions());
      if (selectedCourseId !== undefined) {
        const lessonResult = await listLearningLessons(selectedCourseId);
        setLessons(lessonResult.lessons);
      } else {
        setLessons([]);
      }
    } catch (loadError) {
      setContentError('Unable to load iView Learning content. Check the server API configuration.');
      console.error(loadError);
    } finally {
      setContentLoading(false);
    }
  };

  const loadApprovedTutorials = async () => {
    try {
      setDefinitions(await listTutorialDefinitions());
    } catch (loadError) {
      setContentError('Unable to load approved tutorials.');
      console.error(loadError);
    }
  };

  const publishTutorialDefinition = async (definition: TutorialDefinition) => {
    try {
      const updated = await updateTutorialDefinitionStatus(definition.id, definition.status === 'published' ? 'draft' : 'published');
      setDefinitions((current) => current.map((item) => item.id === updated.id ? updated : item));
    } catch (publishError) {
      setContentError('Unable to update tutorial publication status.');
      console.error(publishError);
    }
  };

  const removeTutorialDefinition = async (definition: TutorialDefinition) => {
    if (!window.confirm(`Remove the draft tutorial "${definition.title}"? This cannot be undone.`)) return;
    try {
      await deleteTutorialDefinition(definition.id);
      setDefinitions((current) => current.filter((item) => item.id !== definition.id));
      setContentMessage('Draft tutorial removed.');
    } catch (deleteError) {
      setContentError('Unable to remove this draft tutorial.');
      console.error(deleteError);
    }
  };

  useEffect(() => {
    if (activeTab !== 'content') return;
    void loadLearningContent();
  }, [activeTab, selectedCourseId]);

  useEffect(() => {
    if (activeTab !== 'approved') return;
    void loadApprovedTutorials();
  }, [activeTab]);

  // The result banner sits above the lesson list, so bring it into view after a generate click.
  useEffect(() => {
    if (contentError || contentMessage) contentFeedbackRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [contentError, contentMessage]);

  const generateSteps = async (lesson: LearningLesson, source: TutorialStepSource) => {
    setGeneratingStepsKey(`${lesson.id}:${source}`);
    setContentError('');
    setContentMessage('');
    try {
      const definition = await generateTutorialSteps({
        source,
        lessonId: lesson.id,
        tutorialKey: `learning-${lesson.course_id || 'course'}-${lesson.id}${source === 'subtitles' ? '-subtitles' : ''}`,
        appName: lesson.course_title || 'iView Learning',
        title: lesson.title.trim(),
      });
      setDefinitions((current) => [definition, ...current]);
      setContentMessage(`Steps generated from the lesson ${source === 'pdf' ? 'PDF' : 'subtitles'} as a draft. Review them before publishing.`);
    } catch (generationError) {
      const apiMessage = axios.isAxiosError(generationError) ? generationError.response?.data?.error?.message : undefined;
      setContentError(apiMessage || 'Unable to generate steps for this lesson.');
      console.error(generationError);
    } finally {
      setGeneratingStepsKey(null);
    }
  };

  const visibleRuns = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    if (!query) return runs;

    return runs.filter((run) =>
      [run.title, run.tutorialKey, run.appName, run.installation.clientId, run.failureReasonCode]
        .some((value) => value?.toLowerCase().includes(query))
    );
  }, [runs, filters.search]);

  const summary = useMemo(
    () => ({
      total: runs.length,
      completed: runs.filter((run) => run.status === 'completed').length,
      failed: runs.filter((run) => run.status === 'failed').length,
      inProgress: runs.filter((run) => run.status === 'in_progress').length,
      steps: runs.reduce((count, run) => count + run.steps.length, 0),
    }),
    [runs]
  );

  const insights = useMemo(() => {
    if (visibleRuns.length === 0) {
      return {
        completionRate: null as number | null,
        avgRating: null as number | null,
        stepCompletionRate: null as number | null,
        topFailureReasons: [] as Array<{ reason: string; count: number }>,
      };
    }

    const completedRuns = visibleRuns.filter((run) => run.status === 'completed').length;
    const ratedRuns = visibleRuns.filter((run) => typeof run.rating === 'number');
    const averageRating = ratedRuns.length > 0
      ? ratedRuns.reduce((sum, run) => sum + Number(run.rating), 0) / ratedRuns.length
      : null;

    const totalSteps = visibleRuns.reduce((count, run) => count + run.steps.length, 0);
    const completedSteps = visibleRuns.reduce(
      (count, run) => count + run.steps.filter((step) => step.completed).length,
      0
    );

    const failureReasonCounts = new Map<string, number>();
    for (const run of visibleRuns) {
      if (!run.failureReasonCode) continue;
      const current = failureReasonCounts.get(run.failureReasonCode) || 0;
      failureReasonCounts.set(run.failureReasonCode, current + 1);
    }

    const topFailureReasons = Array.from(failureReasonCounts.entries())
      .map(([reason, count]) => ({ reason, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      completionRate: completedRuns / visibleRuns.length,
      avgRating: averageRating,
      stepCompletionRate: totalSteps > 0 ? completedSteps / totalSteps : null,
      topFailureReasons,
    };
  }, [visibleRuns]);

  const groupedRuns = useMemo(() => {
    if (groupBy === 'none') {
      return [{ key: 'all', label: null as string | null, runs: visibleRuns }];
    }

    const groups = new Map<string, TutorialRun[]>();
    for (const run of visibleRuns) {
      const software = run.appName || 'Unknown software';
      const existing = groups.get(software) || [];
      existing.push(run);
      groups.set(software, existing);
    }

    return Array.from(groups.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([label, grouped]) => ({ key: label, label, runs: grouped }));
  }, [visibleRuns, groupBy]);

  const visibleIntelligenceRows = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    if (!query) return intelligenceRows;

    return intelligenceRows.filter((row) => (
      `${row.title} ${row.tutorialKey} ${row.appName}`.toLowerCase().includes(query)
    ));
  }, [intelligenceRows, filters.search]);

  useEffect(() => {
    if (groupBy !== 'software') {
      setExpandedSoftware([]);
      return;
    }

    const softwareKeys = groupedRuns
      .filter((group) => group.label)
      .map((group) => group.key);

    setExpandedSoftware((current) => {
      if (current.length === 0) {
        return softwareKeys;
      }

      const kept = current.filter((key) => softwareKeys.includes(key));
      const missing = softwareKeys.filter((key) => !kept.includes(key));
      return [...kept, ...missing];
    });
  }, [groupBy, groupedRuns]);

  const toggleSoftwareGroup = (softwareKey: string) => {
    setExpandedSoftware((current) => (
      current.includes(softwareKey)
        ? current.filter((key) => key !== softwareKey)
        : [...current, softwareKey]
    ));
  };

  const renderRunRows = (run: TutorialRun, nested = false) => {
    const isExpanded = expanded === run.id;
    const completedSteps = run.steps.filter((step) => step.completed).length;

    return (
      <React.Fragment key={run.id}>
        <tr
          className={`${isExpanded ? 'run-row expanded' : 'run-row'}${nested ? ' nested-run-row' : ''}`}
          onClick={() => setExpanded(isExpanded ? null : run.id)}
        >
          <td className="expand-cell">{isExpanded ? <ChevronDown size={17} /> : <ChevronRight size={17} />}</td>
          <td>
            <strong>{run.title}</strong>
            <span className="cell-subtitle">{run.tutorialKey} · {run.appName}</span>
          </td>
          <td>
            <strong>{run.installation.clientId}</strong>
            <span className="cell-subtitle">
              {run.installation.platform || 'Unknown'} · {run.installation.clientVersion || 'Version unknown'}
            </span>
          </td>
          <td>{formatDate(run.startedAt)}</td>
          <td>
            <span className={`tutorial-status ${run.status}`}><span />{statusLabel(run.status)}</span>
            {run.failureReasonCode && <span className="failure-code">{run.failureReasonCode}</span>}
          </td>
          <td>
            <strong>{completedSteps}/{run.steps.length}</strong>
            <span className="cell-subtitle">steps completed</span>
          </td>
          <td>
            {run.rating ? (
              <span className="rating"><Star size={14} fill="currentColor" /> {run.rating}/5</span>
            ) : (
              <span className="muted">Not rated</span>
            )}
          </td>
        </tr>

        {isExpanded && (
          <tr className="detail-row">
            <td colSpan={7}>
              <div className="run-detail">
                <div className="detail-meta">
                  <div><span>Run ID</span><code>{run.id}</code></div>
                  <div><span>Started</span><strong>{formatDate(run.startedAt)}</strong></div>
                  <div><span>Completed</span><strong>{formatDate(run.completedAt)}</strong></div>
                  <div>
                    <span>Locale / architecture</span>
                    <strong>{run.installation.locale || 'Unknown'} / {run.installation.architecture || 'Unknown'}</strong>
                  </div>
                </div>

                <div className="steps-heading">
                  <strong>Step outcomes</strong>
                  <span>{completedSteps} of {run.steps.length} complete</span>
                </div>

                {run.steps.length === 0 ? (
                  <div className="no-steps">No step events were recorded for this run.</div>
                ) : (
                  <ol className="steps-list">
                    {run.steps.map((step) => {
                      const shownCount = getShownHotspotCount(step.shownHotspots);
                      return (
                        <li key={step.id} className={step.completed ? 'step-complete' : 'step-failed'}>
                          <span className="step-icon">
                            {step.completed ? <CheckCircle size={17} /> : <XCircle size={17} />}
                          </span>
                          <div>
                            <strong>Step {step.stepIndex + 1}</strong>
                            <span>
                              {step.completionMethod || 'failed'} · {formatDate(step.completedAt)}
                            </span>
                            {step.stepText && <span>{step.stepText}</span>}
                            {step.failureReasonCode && <span>Reason: {step.failureReasonCode}</span>}
                            {step.rankerVersion && <span>Ranker: {step.rankerVersion}</span>}
                            <span>Shown hotspots: {shownCount}</span>
                            <span>Clicked: {getHotspotSummary(step.clickedHotspot)}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const resetFilters = () => {
    setFilters({ search: '', status: '', platform: '', from: '', to: '' });
    setPage(0);
  };

  return (
    <div className="tutorials-page">
      <div className="page-header tutorials-header">
        <div>
          <div className="tutorials-eyebrow">
            <GraduationCap size={15} /> Learning activity
          </div>
          <h2>Tutorial history</h2>
          <p>Every tutorial run across all installations, with outcomes and step-level detail.</p>
        </div>
        <button className="tutorial-refresh" onClick={() => void loadRuns()} title="Refresh tutorial history">
          <RotateCcw size={16} /> Refresh
        </button>
      </div>

      <div className="tutorial-tabs" role="tablist" aria-label="Tutorial views">
        <button
          className={activeTab === 'history' ? 'active' : ''}
          onClick={() => setActiveTab('history')}
          role="tab"
          aria-selected={activeTab === 'history'}
        >
          Tutorial history
        </button>
        <button
          className={activeTab === 'intelligence' ? 'active' : ''}
          onClick={() => setActiveTab('intelligence')}
          role="tab"
          aria-selected={activeTab === 'intelligence'}
        >
          Tutorial intelligence
        </button>
        <button
          className={activeTab === 'content' ? 'active' : ''}
          onClick={() => setActiveTab('content')}
          role="tab"
          aria-selected={activeTab === 'content'}
        >
          Generate content
        </button>
        <button
          className={activeTab === 'approved' ? 'active' : ''}
          onClick={() => setActiveTab('approved')}
          role="tab"
          aria-selected={activeTab === 'approved'}
        >
          Approved tutorials
        </button>
      </div>

      <div className="tutorial-summary">
        <div><span>Runs on this page</span><strong>{summary.total}</strong></div>
        <div className="summary-complete"><span>Completed</span><strong>{summary.completed}</strong></div>
        <div className="summary-failed"><span>Failed</span><strong>{summary.failed}</strong></div>
        <div className="summary-progress"><span>In progress</span><strong>{summary.inProgress}</strong></div>
        <div><span>Steps observed</span><strong>{summary.steps}</strong></div>
      </div>

      <section className="tutorial-insights" aria-label="Tutorial insights">
        <div className="tutorial-insights-heading">
          <strong>Insights for current view</strong>
          <span>Calculated from currently filtered runs.</span>
        </div>
        <div className="tutorial-insights-grid">
          <div>
            <span>Run completion rate</span>
            <strong>{insights.completionRate === null ? 'N/A' : `${Math.round(insights.completionRate * 100)}%`}</strong>
          </div>
          <div>
            <span>Average rating</span>
            <strong>{insights.avgRating === null ? 'N/A' : `${insights.avgRating.toFixed(1)}/5`}</strong>
          </div>
          <div>
            <span>Step completion rate</span>
            <strong>{insights.stepCompletionRate === null ? 'N/A' : `${Math.round(insights.stepCompletionRate * 100)}%`}</strong>
          </div>
          <div>
            <span>Top failure reasons</span>
            <ul>
              {insights.topFailureReasons.length === 0 ? (
                <li>No failures in current view</li>
              ) : (
                insights.topFailureReasons.map((item) => (
                  <li key={item.reason}>{item.reason} ({item.count})</li>
                ))
              )}
            </ul>
          </div>
        </div>
      </section>

      <section className="tutorial-filters" aria-label="Tutorial filters">
        <div className="filter-heading"><Filter size={16} /><strong>Filter activity</strong></div>
        <label className="filter-search">
          <Search size={16} />
          <input
            value={filters.search}
            onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            placeholder="Search tutorial, app, client ID..."
          />
        </label>
        <select
          value={filters.status}
          onChange={(event) => {
            setFilters({ ...filters, status: event.target.value });
            setPage(0);
          }}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="in_progress">In progress</option>
        </select>
        <select
          value={filters.platform}
          onChange={(event) => {
            setFilters({ ...filters, platform: event.target.value });
            setPage(0);
          }}
          aria-label="Filter by platform"
        >
          <option value="">All platforms</option>
          <option value="darwin">macOS</option>
          <option value="win32">Windows</option>
          <option value="linux">Linux</option>
        </select>
        <label className="group-by-control">
          <span>Group by</span>
          <select
            value={groupBy}
            onChange={(event) => setGroupBy(event.target.value as 'none' | 'software')}
            aria-label="Group runs"
          >
            <option value="none">None</option>
            <option value="software">Software</option>
          </select>
        </label>
        <label>
          <span>From</span>
          <input
            type="date"
            value={filters.from}
            onChange={(event) => {
              setFilters({ ...filters, from: event.target.value });
              setPage(0);
            }}
          />
        </label>
        <label>
          <span>To</span>
          <input
            type="date"
            value={filters.to}
            onChange={(event) => {
              setFilters({ ...filters, to: event.target.value });
              setPage(0);
            }}
          />
        </label>
        <button className="filter-reset" onClick={resetFilters}>Clear</button>
      </section>

      {error && <div className="tutorial-error">{error}</div>}

      {intelligenceError && activeTab === 'intelligence' && <div className="tutorial-error">{intelligenceError}</div>}

      {activeTab === 'history' ? (
        <>
          <section className="tutorial-table-wrap">
            {loading ? (
              <div className="tutorial-empty"><Clock3 size={22} /><strong>Loading tutorial history...</strong></div>
            ) : visibleRuns.length === 0 ? (
              <div className="tutorial-empty">
                <GraduationCap size={24} />
                <strong>No tutorial runs found</strong>
                <span>Try widening the date range or clearing a filter.</span>
              </div>
            ) : (
              <table className="tutorial-table">
                <thead>
                  <tr>
                    <th aria-label="Expand" />
                    <th>Tutorial</th>
                    <th>Installation</th>
                    <th>Started</th>
                    <th>Outcome</th>
                    <th>Steps</th>
                    <th>Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedRuns.map((group) => (
                    <React.Fragment key={group.key}>
                      {group.label ? (
                        <>
                          <tr className="software-row" onClick={() => toggleSoftwareGroup(group.key)}>
                            <td className="expand-cell">
                              {expandedSoftware.includes(group.key) ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
                            </td>
                            <td colSpan={6}>
                              <strong>{group.label}</strong>
                              <span>{group.runs.length} tutorials</span>
                            </td>
                          </tr>
                          {expandedSoftware.includes(group.key) && group.runs.map((run) => renderRunRows(run, true))}
                        </>
                      ) : (
                        group.runs.map((run) => renderRunRows(run))
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <div className="tutorial-pagination">
            <span>Page {page + 1}</span>
            <div>
              <button disabled={page === 0 || loading} onClick={() => setPage(page - 1)}>Previous</button>
              <button disabled={!pagination.hasMore || loading} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          </div>
        </>
      ) : activeTab === 'intelligence' ? (
        <section className="tutorial-table-wrap intelligence-wrap">
          <div className={`intelligence-controls${isTuningCollapsed ? ' collapsed' : ''}`}>
            <div className="intelligence-controls-heading">
              <strong>Confidence tuning</strong>
              <div className="intelligence-controls-actions">
                <button type="button" onClick={() => setIntelligenceConfig(defaultIntelligenceConfig)}>Reset defaults</button>
                <button
                  type="button"
                  className="collapse-toggle"
                  aria-expanded={!isTuningCollapsed}
                  onClick={() => setIsTuningCollapsed((current) => !current)}
                >
                  <span className={`collapse-toggle-icon${isTuningCollapsed ? ' collapsed' : ''}`}>
                    <ChevronDown size={12} />
                  </span>
                  {isTuningCollapsed ? 'Show tuning' : 'Hide tuning'}
                </button>
              </div>
            </div>

            {!isTuningCollapsed && (
              <>
                <div className="intelligence-presets" role="group" aria-label="Confidence presets">
                  <button
                    type="button"
                    className={intelligenceConfigEquals(intelligenceConfig, intelligencePresets.strict) ? 'active' : ''}
                    onClick={() => setIntelligenceConfig(intelligencePresets.strict)}
                  >
                    Strict
                  </button>
                  <button
                    type="button"
                    className={intelligenceConfigEquals(intelligenceConfig, intelligencePresets.balanced) ? 'active' : ''}
                    onClick={() => setIntelligenceConfig(intelligencePresets.balanced)}
                  >
                    Balanced
                  </button>
                  <button
                    type="button"
                    className={intelligenceConfigEquals(intelligenceConfig, intelligencePresets.lenient) ? 'active' : ''}
                    onClick={() => setIntelligenceConfig(intelligencePresets.lenient)}
                  >
                    Lenient
                  </button>
                </div>

                <div className="intelligence-controls-grid">
                  <label>
                    <span>Minimum runs</span>
                    <input
                      type="number"
                      min={1}
                      max={50}
                      value={intelligenceConfig.minRuns}
                      onChange={(event) => setIntelligenceConfig({
                        ...intelligenceConfig,
                        minRuns: Math.max(1, Number(event.target.value) || 1),
                      })}
                    />
                  </label>
                  <label>
                    <span>Confidence threshold %</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round(intelligenceConfig.confidenceThreshold * 100)}
                      onChange={(event) => setIntelligenceConfig({
                        ...intelligenceConfig,
                        confidenceThreshold: Math.min(1, Math.max(0, (Number(event.target.value) || 0) / 100)),
                      })}
                    />
                  </label>
                  <label>
                    <span>Completion threshold %</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round(intelligenceConfig.completionThreshold * 100)}
                      onChange={(event) => setIntelligenceConfig({
                        ...intelligenceConfig,
                        completionThreshold: Math.min(1, Math.max(0, (Number(event.target.value) || 0) / 100)),
                      })}
                    />
                  </label>
                  <label>
                    <span>Step threshold %</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round(intelligenceConfig.stepThreshold * 100)}
                      onChange={(event) => setIntelligenceConfig({
                        ...intelligenceConfig,
                        stepThreshold: Math.min(1, Math.max(0, (Number(event.target.value) || 0) / 100)),
                      })}
                    />
                  </label>
                  <label>
                    <span>Hotspot threshold %</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round(intelligenceConfig.hotspotThreshold * 100)}
                      onChange={(event) => setIntelligenceConfig({
                        ...intelligenceConfig,
                        hotspotThreshold: Math.min(1, Math.max(0, (Number(event.target.value) || 0) / 100)),
                      })}
                    />
                  </label>
                  <label>
                    <span>Flow threshold %</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round(intelligenceConfig.flowThreshold * 100)}
                      onChange={(event) => setIntelligenceConfig({
                        ...intelligenceConfig,
                        flowThreshold: Math.min(1, Math.max(0, (Number(event.target.value) || 0) / 100)),
                      })}
                    />
                  </label>
                  <label>
                    <span>Rating threshold %</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round(intelligenceConfig.ratingThreshold * 100)}
                      onChange={(event) => setIntelligenceConfig({
                        ...intelligenceConfig,
                        ratingThreshold: Math.min(1, Math.max(0, (Number(event.target.value) || 0) / 100)),
                      })}
                    />
                  </label>
                </div>

                <div className="intelligence-weights-grid">
                  <label>
                    <span>Completion weight</span>
                    <input
                      type="number"
                      min={0}
                      step={0.05}
                      value={intelligenceConfig.completionWeight}
                      onChange={(event) => setIntelligenceConfig({
                        ...intelligenceConfig,
                        completionWeight: Math.max(0, Number(event.target.value) || 0),
                      })}
                    />
                  </label>
                  <label>
                    <span>Step weight</span>
                    <input
                      type="number"
                      min={0}
                      step={0.05}
                      value={intelligenceConfig.stepWeight}
                      onChange={(event) => setIntelligenceConfig({
                        ...intelligenceConfig,
                        stepWeight: Math.max(0, Number(event.target.value) || 0),
                      })}
                    />
                  </label>
                  <label>
                    <span>Hotspot weight</span>
                    <input
                      type="number"
                      min={0}
                      step={0.05}
                      value={intelligenceConfig.hotspotWeight}
                      onChange={(event) => setIntelligenceConfig({
                        ...intelligenceConfig,
                        hotspotWeight: Math.max(0, Number(event.target.value) || 0),
                      })}
                    />
                  </label>
                  <label>
                    <span>Flow weight</span>
                    <input
                      type="number"
                      min={0}
                      step={0.05}
                      value={intelligenceConfig.flowWeight}
                      onChange={(event) => setIntelligenceConfig({
                        ...intelligenceConfig,
                        flowWeight: Math.max(0, Number(event.target.value) || 0),
                      })}
                    />
                  </label>
                  <label>
                    <span>Rating weight</span>
                    <input
                      type="number"
                      min={0}
                      step={0.05}
                      value={intelligenceConfig.ratingWeight}
                      onChange={(event) => setIntelligenceConfig({
                        ...intelligenceConfig,
                        ratingWeight: Math.max(0, Number(event.target.value) || 0),
                      })}
                    />
                  </label>
                </div>
              </>
            )}
          </div>

          {intelligenceLoading ? (
            <div className="tutorial-empty"><Clock3 size={22} /><strong>Calculating tutorial intelligence...</strong></div>
          ) : visibleIntelligenceRows.length === 0 ? (
            <div className="tutorial-empty">
              <GraduationCap size={24} />
              <strong>No intelligence results yet</strong>
              <span>Collect more tutorial runs or broaden your filters.</span>
            </div>
          ) : (
            <table className="tutorial-table intelligence-table">
              <thead>
                <tr>
                  <th>Tutorial</th>
                  <th>Runs</th>
                  <th>Confidence</th>
                  <th>Completion</th>
                  <th>Step flow</th>
                  <th>Hotspot quality</th>
                  <th>Rating quality</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {visibleIntelligenceRows.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <strong>{row.title}</strong>
                      <span className="cell-subtitle">{row.tutorialKey} · {row.appName}</span>
                      {row.topFailureReason && <span className="cell-subtitle">Top failure: {row.topFailureReason}</span>}
                    </td>
                    <td>{row.runs}</td>
                    <td>
                      <span className={`confidence-pill ${row.confidenceLabel}`}>
                        {Math.round(row.confidenceScore * 100)}%
                      </span>
                    </td>
                    <td>{Math.round(row.completionRate * 100)}%</td>
                    <td>{Math.round(row.flowQualityScore * 100)}%</td>
                    <td>{Math.round(row.hotspotQualityScore * 100)}%</td>
                    <td>{Math.round(row.ratingScore * 100)}%</td>
                    <td>
                      <span className={`intelligence-status ${row.workingCorrectly ? 'good' : 'needs-work'}`}>
                        {row.workingCorrectly ? 'Working correctly' : 'Needs improvement'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ) : activeTab === 'content' ? (
        <section className="learning-content" aria-label="iView Learning content">
          <div className="learning-content-header">
            <div>
              <strong><BookOpen size={16} /> iView Learning library</strong>
              <span>Generate timed tutorial steps from a lesson's subtitles, then review and publish them for the desktop app.</span>
            </div>
            <button className="tutorial-refresh" onClick={() => void loadLearningContent()} disabled={contentLoading} title="Refresh iView Learning content">
              <RotateCcw size={16} /> Refresh
            </button>
          </div>
          <div className="learning-content-toolbar">
            <label className="learning-search">
              <Search size={16} />
              <input value={contentSearch} onChange={(event) => setContentSearch(event.target.value)} placeholder="Search courses..." />
            </label>
            <button className="btn btn-secondary" onClick={() => void loadLearningContent()} disabled={contentLoading}>Search</button>
          </div>
          <div ref={contentFeedbackRef}>
            {contentError && <div className="tutorial-error">{contentError}</div>}
            {contentMessage && <div className="learning-success">{contentMessage}</div>}
          </div>
          <div className="tutorial-definition-workspace">
            <div className="learning-section-label">Desktop tutorial definitions <span>{definitions.length}</span></div>
            <p className="definition-helper">Choose “From PDF” or “From subtitles” beside a lesson below. PDF steps are written by the configured AI provider; each English subtitle becomes one subtitle step. Both are saved as drafts for review.</p>
            {definitions.filter((definition) => definition.status !== 'published').length > 0 && (
              <div className="tutorial-definition-list">
                {definitions.filter((definition) => definition.status !== 'published').map((definition) => (
                  <div className="tutorial-definition-row" key={definition.id}>
                    <div><strong>{definition.title}</strong><span>{definition.appName} · {definition.steps.length} steps · {definition.status}</span></div>
                    <div className="tutorial-definition-actions"><Link className="btn btn-secondary" to={`/tutorials/${definition.id}/edit`}>Edit</Link><button className="btn btn-primary" onClick={() => void publishTutorialDefinition(definition)}>Publish</button><button className="icon-btn danger" onClick={() => void removeTutorialDefinition(definition)} aria-label={`Remove ${definition.title}`} title="Remove draft"><Trash2 size={15} /></button></div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {contentLoading ? (
            <div className="tutorial-empty"><Clock3 size={22} /><strong>Loading iView Learning content...</strong></div>
          ) : courses.length === 0 ? (
            <div className="tutorial-empty"><BookOpen size={24} /><strong>No courses found</strong><span>Check the API key and search filters.</span></div>
          ) : (
            <div className="learning-content-grid">
              <div className="learning-courses">
                <div className="learning-section-label">Courses <span>{courses.length}</span></div>
                {courses.map((course) => (
                  <button key={course.id} className={selectedCourseId === course.id ? 'learning-course active' : 'learning-course'} onClick={() => setSelectedCourseId(course.id)}>
                    <strong>{course.title}</strong>
                    <span>{course.platform || 'Platform unknown'} · {course.lesson_count ?? '—'} lessons</span>
                  </button>
                ))}
              </div>
              <div className="learning-lessons">
                {selectedCourseId === undefined ? (
                  <div className="tutorial-empty"><BookOpen size={22} /><strong>Select a course</strong><span>Its lessons and generation actions will appear here.</span></div>
                ) : lessons.length === 0 ? (
                  <div className="tutorial-empty"><BookOpen size={22} /><strong>No lessons found</strong></div>
                ) : lessons.map((lesson) => (
                  <article className="learning-lesson" key={lesson.id}>
                    <div><strong>{lesson.title}</strong><span>Lesson {lesson.id} · {lesson.videotype || 'No video type'}</span></div>
                    <div className="learning-lesson-actions">
                      {lesson.pdf_url && (
                        <button className="learning-pdf-link" onClick={() => setPdfPreview({ title: lesson.title, url: lesson.pdf_url as string })}>
                          View PDF
                        </button>
                      )}
                      {([
                        { source: 'pdf', label: 'From PDF', available: Boolean(lesson.pdf_url), missing: 'This lesson has no PDF' },
                        { source: 'subtitles', label: 'From subtitles', available: lesson.has_subtitles, missing: 'This lesson has no English subtitles' },
                      ] as const).map((option) => (
                        <button
                          key={option.source}
                          className="btn btn-primary pdf-tutorial-button"
                          onClick={() => void generateSteps(lesson, option.source)}
                          disabled={generatingStepsKey !== null || !option.available}
                          title={option.available ? `Generate steps ${option.label.toLowerCase()}` : option.missing}
                        >
                          {generatingStepsKey === `${lesson.id}:${option.source}` ? 'Generating...' : option.label}
                        </button>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
        </section>
      ) : (
        <section className="tutorial-table-wrap approved-tutorials-wrap">
          <div className="approved-tutorials-header">
            <div>
              <strong>Approved tutorials</strong>
              <span>Published tutorials available to the desktop app.</span>
            </div>
            <button className="tutorial-refresh" onClick={() => void loadApprovedTutorials()} title="Refresh approved tutorials">
              <RotateCcw size={16} /> Refresh
            </button>
          </div>
          {contentError ? (
            <div className="tutorial-error">{contentError}</div>
          ) : definitions.filter((definition) => definition.status === 'published').length === 0 ? (
            <div className="tutorial-empty"><GraduationCap size={24} /><strong>No approved tutorials</strong><span>Publish a generated tutorial to make it available to the desktop app.</span></div>
          ) : (
            <table className="tutorial-table approved-tutorials-table">
              <thead><tr><th aria-label="Expand" /><th>Tutorial</th><th>Software</th><th>Steps</th><th>Source PDF</th><th>Published</th><th>Status</th></tr></thead>
              <tbody>{definitions.filter((definition) => definition.status === 'published').map((definition) => (
                <React.Fragment key={definition.id}>
                <tr className={`run-row${expandedDefinition === definition.id ? ' expanded' : ''}`} onClick={() => setExpandedDefinition(expandedDefinition === definition.id ? null : definition.id)}>
                  <td className="expand-cell"><ChevronRight className={expandedDefinition === definition.id ? 'definition-chevron expanded' : 'definition-chevron'} size={17} /></td>
                  <td><strong>{definition.title}</strong><span className="cell-subtitle">{definition.tutorialKey}</span></td>
                  <td>{definition.appName}</td>
                  <td><strong>{definition.steps.length}</strong><span className="cell-subtitle">generated steps</span></td>
                  <td>{definition.sourcePdfUrl ? <button className="learning-pdf-link" onClick={(event) => { event.stopPropagation(); setPdfPreview({ title: definition.title, url: definition.sourcePdfUrl as string }); }}>View PDF</button> : <span className="muted">Not attached</span>}</td>
                  <td>{formatDate(definition.publishedAt)}</td>
                  <td><span className="tutorial-status completed"><span />Approved</span></td>
                </tr>
                {expandedDefinition === definition.id && (
                  <tr className="detail-row approved-definition-detail"><td colSpan={7}><div className="run-detail"><div className="steps-heading"><strong>Generated tutorial steps</strong><span>{definition.steps.length} steps</span></div><ol className="steps-list">{definition.steps.map((step) => <li className="step-complete" key={step.stepIndex}><span className="step-icon"><CheckCircle size={17} /></span><div><strong>Step {step.stepIndex + 1}</strong>{step.startTime !== undefined && step.endTime !== undefined && <small className="muted">Video {formatVideoTime(step.startTime)}–{formatVideoTime(step.endTime)}</small>}<span>{step.instruction}</span>{step.target && <code>{JSON.stringify(step.target)}</code>}{step.images && step.images.length > 0 && <div className="definition-images"><span>Source page {step.sourcePage || step.images[0].pageNumber}</span>{step.images.map((image) => <img key={`${image.pageNumber}-${image.name}`} src={image.dataUrl} alt={`Source PDF page ${image.pageNumber}`} />)}</div>}</div></li>)}</ol></div></td></tr>
                )}
                </React.Fragment>
              ))}</tbody>
            </table>
          )}
        </section>
      )}
      {pdfPreview && <PdfPreviewModal title={pdfPreview.title} url={pdfPreview.url} onClose={() => setPdfPreview(null)} />}
    </div>
  );
}
